<template>
	<Loader v-if="isLoading" />
	<div class="home">
		<Navbar
			v-if="links.length"
			:links="links"
			@trigger-lang="toggleLanguage"
		/>

		<b-button
			v-if="showScrollTop"
			class="btn-scrolltop"
			@click="scrollToTop"
		>
			<SvgIcon
				type="mdi"
				:path="icons.arrowUp"
			/>
		</b-button>

		<b-button
			class="btn-whatsapp"
			@click="openWhatsapp"
		>
			<SvgIcon
				type="mdi"
				:path="icons.whatsapp"
			/>
		</b-button>

		<div class="header">
			<div class="elementor-shape elementor-shape-top">
				<svg
					xmlns="http://www.w3.org/2000/svg"
					viewBox="0 0 2600 131.1"
					preserveAspectRatio="none"
				>
					<path
						class="elementor-shape-fill"
						d="M0 0L2600 0 2600 69.1 0 0z"
					/>
					<path
						class="elementor-shape-fill"
						style="opacity: 0.5"
						d="M0 0L2600 0 2600 69.1 0 69.1z"
					/>
					<path
						class="elementor-shape-fill"
						style="opacity: 0.25"
						d="M2600 0L0 0 0 130.1 2600 69.1z"
					/>
				</svg>
			</div>

			<div class="elementor-shape elementor-shape-bottom">
				<svg
					xmlns="http://www.w3.org/2000/svg"
					viewBox="0 0 2600 131.1"
					preserveAspectRatio="none"
				>
					<path
						class="elementor-shape-fill"
						d="M0 0L2600 0 2600 69.1 0 0z"
					/>
					<path
						class="elementor-shape-fill"
						style="opacity: 0.5"
						d="M0 0L2600 0 2600 69.1 0 69.1z"
					/>
					<path
						class="elementor-shape-fill"
						style="opacity: 0.25"
						d="M2600 0L0 0 0 130.1 2600 69.1z"
					/>
				</svg>
			</div>

			<div class="parallax">
				<p class="title mb-0">{{ pageTitle }}</p>
				<div class="desc">{{ pageDescription }}</div>
			</div>
		</div>

		<div class="filter">
			<div class="row">
				<div class="col-12 service">
					<div class="justify-content-center align-item-center d-flex">
						<p class="mb-0">Home Visit</p>
						<b-form-checkbox
							v-model="filter.isBoarding"
							switch
							class="mx-3"
						/>
						<p class="mb-0">Home Boarding</p>
					</div>
				</div>

				<div class="col-12 city text-center">
					<b-button
						v-for="city in filter.cityOption"
						:key="city"
						:class="{ active: city === filter.city, 'me-2': true }"
						@click="selectCity(city)"
					>
						{{ city }}
					</b-button>
				</div>
			</div>
		</div>

		<div class="sitters">
			<div class="row">
				<div
					v-for="(sitter, index) in filteredSitters"
					:key="`sitter-${sitter.id || index}`"
					class="col-6 col-lg-3 text-center"
				>
					<div class="card">
						<img
							:src="getImageUrl(sitter)"
							:alt="`${sitter.name} profile`"
							class="img-fluid"
							draggable="false"
						/>

						<div class="row">
							<div class="col-12">
								<a
									:href="getInstagramUrl(sitter.ig)"
									target="_blank"
									rel="noopener noreferrer"
								>
									<SvgIcon
										type="mdi"
										:path="icons.instagram"
										class="instagram"
									/>
								</a>
								<p class="name mb-0">{{ sitter.name }}</p>
							</div>
						</div>

						<div class="row">
							<div class="col-12">
								<p
									class="d-flex align-items-center justify-content-center mb-0"
								>
									{{ sitter.city?.name }} || {{ sitter.rating }}
									<SvgIcon
										type="mdi"
										:path="icons.star"
										size="20"
										class="star"
									/>
								</p>
								<p class="mb-0">{{ getServiceTitle(sitter) }}</p>
								<p class="mb-0">{{ sitter.level?.name }}</p>
							</div>
						</div>

						<div
							class="accordion"
							:id="`accordionSitters${index}`"
						>
							<div class="accordion-item">
								<h2 class="accordion-header">
									<button
										class="accordion-button collapsed"
										type="button"
										data-bs-toggle="collapse"
										:data-bs-target="`#collapseIntro${index}`"
										:aria-controls="`collapseIntro${index}`"
									>
										{{ introductionLabel }}
									</button>
								</h2>
								<div
									:id="`collapseIntro${index}`"
									class="accordion-collapse collapse"
									:data-bs-parent="`#accordionSitters${index}`"
								>
									<div class="accordion-body">
										<p v-html="sitter.intro" />
									</div>
								</div>
							</div>

							<div class="accordion-item">
								<h2 class="accordion-header">
									<button
										class="accordion-button collapsed"
										type="button"
										data-bs-toggle="collapse"
										:data-bs-target="`#collapseSkillsExperience${index}`"
										:aria-controls="`collapseSkillsExperience${index}`"
									>
										{{ skillsExperienceLabel }}
									</button>
								</h2>
								<div
									:id="`collapseSkillsExperience${index}`"
									class="accordion-collapse collapse"
									:data-bs-parent="`#accordionSitters${index}`"
								>
									<div class="accordion-body">
										<p
											style="text-align: left"
											v-html="sitter.skill_experience"
										/>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<Footer
			v-if="links.length"
			:links="links"
		/>
	</div>
</template>

<script>
import api from "@/api";
import Navbar from "@/landing/components/Navbar.vue";
import Footer from "@/landing/components/Footer.vue";
import Loader from "@/landing/components/Loader.vue";
import SvgIcon from "@jamescoyle/vue-icon";
import { mdiStar, mdiArrowUpBold, mdiWhatsapp, mdiInstagram } from "@mdi/js";

const API_BASE_URL = "https://catsitterindonesia.com/";
const INSTAGRAM_BASE_URL = "https://www.instagram.com/";
const WHATSAPP_URL =
	"https://api.whatsapp.com/send/?phone=%206287730903400&text=Halo%2C+Saya+tertarik+untuk+memesan+jasa+cat+sitter&app_absent=0";
const SCROLL_THRESHOLD = 600;
const SCROLL_DEBOUNCE_MS = 100;

export default {
	name: "ProfilePage",
	components: {
		Navbar,
		Footer,
		Loader,
		SvgIcon,
	},
	data() {
		return {
			isLoading: true,
			scrollY: 0,
			links: [],
			sitters: [],
			lang: localStorage.getItem("lang") || "en",
			filter: {
				city: null, // Will be set to first city after fetch
				isBoarding: false, // false = Home Visit (service_id 1), true = Boarding (service_id 2)
				cityOption: [],
			},
			icons: {
				star: mdiStar,
				arrowUp: mdiArrowUpBold,
				whatsapp: mdiWhatsapp,
				instagram: mdiInstagram,
			},
			scrollTimer: null,
		};
	},
	computed: {
		pageTitle() {
			return this.lang === "id" ? "Pengasuh Kami" : "Our Sitters";
		},
		pageDescription() {
			return this.lang === "id"
				? "Ketahui lebih banyak tentang pengasuh kucing kami termasuk pengalaman, keterampilan, dan kepribadian mereka yang menawan!"
				: "Get to know more about our cat sitters including their experiences, skills, and lovely personalities!";
		},
		introductionLabel() {
			return this.lang === "id" ? "Pengenalan" : "Introduction";
		},
		skillsExperienceLabel() {
			return this.lang === "id"
				? "Keterampilan & Pengalaman"
				: "Skills & Experience";
		},
		filteredSitters() {
			return this.sitters.filter((sitter) => {
				const cityMatch = sitter.city?.name === this.filter.city;

				const serviceTitle =
					this.lang === "id"
						? sitter.service?.title_id
						: sitter.service?.title_en;

				if (serviceTitle === "All") {
					return cityMatch;
				}

				const serviceId = this.filter.isBoarding ? 2 : 1;
				const serviceMatch = sitter.service_id === serviceId;

				return cityMatch && serviceMatch;
			});
		},
		showScrollTop() {
			return this.scrollY > SCROLL_THRESHOLD;
		},
	},
	methods: {
		capitalizeFirst(str) {
			if (!str) return "";
			return str.charAt(0).toUpperCase() + str.slice(1);
		},
		getImageUrl(sitter) {
			if (!sitter.file?.path)
				return "https://backoffice.catsitterindonesia.com/storage/compro_provider/QFrGnVdvMqZ4wzCylstO1nCR1aBdFtUrCLffj1a1.png";
			return `https://backoffice.catsitterindonesia.com/storage/${sitter.file?.path}`;
		},
		getInstagramUrl(handle) {
			if (!handle) return "#";
			return `${INSTAGRAM_BASE_URL}${handle}`;
		},
		getServiceTitle(sitter) {
			if (!sitter.service) return "";
			const title =
				this.lang === "id" ? sitter.service.title_id : sitter.service.title_en;

			if (title === "All") {
				return "Visit & Boarding";
			}

			return title;
		},
		handleScroll() {
			if (this.scrollTimer) return;
			this.scrollTimer = setTimeout(() => {
				this.scrollY = window.scrollY;
				this.scrollTimer = null;
			}, SCROLL_DEBOUNCE_MS);
		},
		scrollToTop() {
			window.scrollTo({ top: 0, behavior: "smooth" });
		},
		toggleLanguage(newLang) {
			this.lang = newLang || (this.lang === "en" ? "id" : "en");
			localStorage.setItem("lang", this.lang);
		},
		openWhatsapp() {
			window.open(WHATSAPP_URL, "_blank", "noopener,noreferrer");
		},
		selectCity(city) {
			this.filter.city = city;
		},
		async fetchLinks() {
			try {
				const response = await api.get("compro/link");
				this.links = response.data;
			} catch (error) {
				console.error("Error fetching links:", error);
			}
		},
		async fetchSitters() {
			try {
				const response = await api.get("compro/provider");
				const sittersData = response.data;

				const uniqueCities = [
					...new Map(
						sittersData.map((sitter) => [sitter.city.id, sitter.city.name]),
					).values(),
				];
				this.filter.cityOption = uniqueCities;

				if (this.filter.cityOption.length > 0) {
					this.filter.city = this.filter.cityOption[0];
				}

				this.sitters = sittersData;
			} catch (error) {
				console.error("Error fetching sitters:", error);
			} finally {
				this.isLoading = false;
			}
		},
		async initializeData() {
			this.isLoading = true;
			await this.fetchLinks();
			await this.fetchSitters();
		},
	},
	mounted() {
		window.scrollTo(0, 0);
		window.addEventListener("scroll", this.handleScroll, { passive: true });
		this.initializeData();
	},
	beforeUnmount() {
		window.removeEventListener("scroll", this.handleScroll);
		if (this.scrollTimer) {
			clearTimeout(this.scrollTimer);
		}
	},
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.btn-scrolltop {
	position: fixed;
	bottom: 2vw;
	left: 2vw;
	z-index: 1029;
	padding: 1vw;
	border: 0;
	background: #fdb23f;
	border-radius: 50%;
}

.btn-scrolltop:hover {
	background: #ff5a2a;
}

.btn-whatsapp {
	position: fixed;
	bottom: 2vw;
	right: 2vw;
	z-index: 1029;
	padding: 1vw;
	border: 0;
	border-radius: 50%;
}

.btn-whatsapp:hover {
	background: #25d366;
}

.header {
	background-image: url(https://backoffice.catsitterindonesia.com/storage/compro_heading/Yq6zMuA39ZlbLSgXlbG3xSTN3IgxDRonrJDZBEOy.jpg);
	background-size: cover;
	background-attachment: fixed;
	position: relative;
}

.header .elementor-shape {
	overflow: hidden;
	position: absolute;
	left: 0;
	width: 100%;
	line-height: 0;
	direction: ltr;
}

.header .elementor-shape svg {
	width: calc(100% + 1.3px);
	position: relative;
	left: 50%;
	transform: translateX(-50%);
}

.header .elementor-shape-top {
	top: -1px;
}

.header .elementor-shape-bottom {
	transform: rotate(180deg);
	bottom: -1px;
}

.header .elementor-shape-top svg,
.elementor-shape-bottom svg {
	height: 6vw;
}

.header .elementor-shape .elementor-shape-fill {
	fill: #f8eee2;
	transform-origin: center;
	transform: rotateY(0deg);
}

.header .parallax {
	padding: 8vw 10vw;
	text-align: center;
}

.header .parallax .title {
	color: #f8eee2;
	font-size: 8vw;
	font-weight: bold;
	text-align: center;
	text-shadow: 0 0 10px rgba(0, 0, 0, 0.8);
}

.header .parallax .desc {
	color: #f8eee2;
	font-size: 1.25vw;
	font-weight: 600;
	margin-top: 1.56vw;
	text-align: center;
	text-shadow: 0 0 10px rgba(0, 0, 0, 0.8);
}

.filter {
	padding: 4vw 10vw;
}

.filter .service {
	margin-bottom: 2vw;
}

.filter .service p {
	color: #ff5a2a;
	font-size: 1.25vw;
	font-weight: bold;
}

.filter .city button {
	background: #fdb23f;
	border: 0;
	border-radius: 2.5vw;
	color: #fff;
	font-size: 1.25vw;
	font-weight: 600;
	padding: 0.63vw 1.25vw;
}

.filter .city button.active {
	cursor: unset;
}

.filter .city button.active,
.filter .city button:hover,
.filter .city button:active {
	background: #ff5a2a;
}

.sitters {
	padding: 0 10vw;
}

.sitters .col-6 {
	margin-bottom: 2vw;
}

.sitters .card {
	background: #fff;
	border: 0;
	border-radius: 1.25vw;
	box-shadow: 0 0 5px rgba(0, 0, 0, 0.1);
	padding: 1.5vw;
}

.sitters .card img {
	height: 14.5vw;
}

.sitters .card a svg {
	margin-top: 2vw;
}

.sitters .card .name {
	color: #000;
	font-size: 1.3vw;
	font-weight: 600;
}

.sitters .card svg.instagram {
	color: #833ab4;
}

.sitters .card svg.star {
	color: #fcb017;
}

.sitters .card .accordion-item {
	background-color: #fff;
	border: 0;
	border-bottom: 1px solid #f8eee2;
	padding-top: 0.5vw;
	padding-bottom: 0.5vw;
}

.sitters .card .accordion-item:first-of-type .accordion-button {
	border-radius: 0;
}

.sitters .accordion-button {
	background-color: unset;
	padding: 0.2vw 0.5vw;
}

.sitters .card .accordion-button:hover {
	color: #ff5a2a;
}

.sitters .card .accordion-button:active {
	box-shadow: unset;
}

.sitters .card .accordion-button:not(.collapsed) {
	background-color: unset;
	color: #ff5a2a;
	box-shadow: unset;
}

.sitters .card .accordion-body {
	padding: 0.5vw;
	text-align: justify;
}

.sitters .card .accordion-item p {
	font-size: 0.9vw;
	font-weight: unset;
	color: #000;
}

@media (max-width: 991.98px) {
	.btn-scrolltop {
		bottom: 5vw;
		left: 5vw;
		padding: 2vw;
	}

	.btn-whatsapp {
		bottom: 5vw;
		right: 5vw;
		padding: 2vw;
	}

	.header {
		background-position: bottom;
		background-clip: border-box;
	}

	.header .parallax {
		padding-left: 5vw;
		padding-right: 5vw;
	}

	.header .parallax .title {
		font-size: 15vw;
	}

	.header .parallax .desc {
		font-size: 5vw;
		margin-top: 5vw;
	}

	.filter {
		padding: 5vw;
	}

	.filter .service {
		margin-bottom: 5vw;
	}

	.filter .service p {
		font-size: 5vw;
	}

	.filter .city button {
		padding: 2vw 3vw;
		font-size: 3vw;
		border-radius: 10vw;
		margin-bottom: 3vw;
	}

	.sitters {
		padding: 0 5vw;
	}

	.sitters .col-6 {
		margin-bottom: 5vw;
	}

	.sitters .card {
		padding: 3vw;
		border-radius: 3vw;
	}

	.sitters .card img {
		height: 36vw;
	}

	.sitters .card svg {
		height: 5vw;
	}

	.sitters .card .name {
		font-size: 4vw;
		margin-bottom: 1vw;
	}

	.sitters .card p,
	.sitters .card .accordion-button {
		font-size: 3vw;
	}

	.sitters .card .accordion-body p {
		font-size: 2.5vw;
	}
}
</style>
